// ERP Primitives — shared visual components for a modern, premium ERP look.
// Import from anywhere: `import { KpiCard, StatusBadge, HighlightCard, btnVariants } from "@/components/erp/primitives";`
import React from "react";

// ---------------------------------------------------------------------------
// KpiCard — colored top border + subtle tinted background. Used on Dashboard.
// ---------------------------------------------------------------------------
const KPI_VARIANTS = {
  navy: {
    border: "border-t-[#1D2951]",
    tint: "bg-gradient-to-b from-slate-50/70 to-white",
    iconWrap: "bg-[#1D2951]/10 text-[#1D2951]",
  },
  blue: {
    border: "border-t-blue-500",
    tint: "bg-gradient-to-b from-blue-50/60 to-white",
    iconWrap: "bg-blue-100 text-blue-700",
  },
  sky: {
    border: "border-t-sky-500",
    tint: "bg-gradient-to-b from-sky-50/70 to-white",
    iconWrap: "bg-sky-100 text-sky-700",
  },
  emerald: {
    border: "border-t-emerald-500",
    tint: "bg-gradient-to-b from-emerald-50/70 to-white",
    iconWrap: "bg-emerald-100 text-emerald-700",
  },
  amber: {
    border: "border-t-amber-500",
    tint: "bg-gradient-to-b from-amber-50/80 to-white",
    iconWrap: "bg-amber-100 text-amber-700",
  },
  rose: {
    border: "border-t-rose-500",
    tint: "bg-gradient-to-b from-rose-50/70 to-white",
    iconWrap: "bg-rose-100 text-rose-700",
  },
  slate: {
    border: "border-t-slate-400",
    tint: "bg-white",
    iconWrap: "bg-slate-100 text-slate-600",
  },
};

export const KpiCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  variant = "navy",
  testid,
}) => {
  const v = KPI_VARIANTS[variant] || KPI_VARIANTS.navy;
  // Scale the number down when it gets long enough to overflow the card
  // (e.g. crore figures like "₹2,44,55,063"). Monospace + tabular-nums keeps
  // digits aligned across cards even at the smaller sizes.
  const valLen = String(value ?? "").length;
  const valueClass =
    valLen >= 15 ? "text-lg"
    : valLen >= 13 ? "text-xl"
    : valLen >= 11 ? "text-2xl"
    : "text-3xl";
  return (
    <div
      data-testid={testid}
      className={`p-5 rounded-xl border border-slate-200 border-t-4 ${v.border} ${v.tint} shadow-sm hover:shadow-md transition-shadow flex flex-col gap-3 min-w-0 overflow-hidden`}
    >
      <div className="flex items-start justify-between">
        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          {title}
        </span>
        {Icon && (
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${v.iconWrap}`}>
            <Icon size={16} />
          </span>
        )}
      </div>
      <div className={`font-mono tabular-nums tracking-tight ${valueClass} font-semibold text-slate-900 whitespace-nowrap overflow-hidden text-ellipsis`}>
        {value}
      </div>
      {subtitle && (
        <div className="text-[11px] text-slate-500">{subtitle}</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// StatusBadge — pill with tinted background matching the status.
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
  completed: "bg-sky-50 text-sky-800 border-sky-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  open: "bg-amber-50 text-amber-800 border-amber-200",
  partial: "bg-sky-50 text-sky-800 border-sky-200",
  default: "bg-slate-50 text-slate-700 border-slate-200",
};

export const StatusBadge = ({ status, label, testid }) => {
  const key = String(status || "").toLowerCase();
  const cls = STATUS_STYLES[key] || STATUS_STYLES.default;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${cls}`}
    >
      {label || status}
    </span>
  );
};

// ---------------------------------------------------------------------------
// HighlightCard — big callout used for MR (Contribution Margin, EBITDA,
// Operating Profit, EBIT). Auto-picks tone from value when `auto` is set.
// ---------------------------------------------------------------------------
const HIGHLIGHT_TONES = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800 border-l-4 border-l-emerald-500",
  rose:    "bg-rose-50 border-rose-200 text-rose-800 border-l-4 border-l-rose-500",
  sky:     "bg-sky-50 border-sky-200 text-sky-900 border-l-4 border-l-sky-500",
  navy:    "bg-slate-50 border-slate-200 text-slate-900 border-l-4 border-l-[#1D2951]",
  amber:   "bg-amber-50 border-amber-200 text-amber-900 border-l-4 border-l-amber-500",
};

export const HighlightCard = ({
  title,
  subtitle,
  value,
  tone = "emerald",
  auto = false,
  bold = false,
  testid,
}) => {
  // "auto" flips to rose when the value is negative — used for EBIT.
  const finalTone = auto ? (Number(value || 0) < 0 ? "rose" : tone) : tone;
  const cls = HIGHLIGHT_TONES[finalTone] || HIGHLIGHT_TONES.navy;
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border px-5 py-4 my-4 flex items-center justify-between gap-4 shadow-sm ${cls}`}
    >
      <div className="min-w-0">
        <div className={`uppercase tracking-wide font-semibold ${bold ? "text-sm" : "text-xs"}`}>
          {title}
        </div>
        {subtitle && (
          <div className="text-[11px] mt-0.5 opacity-80">{subtitle}</div>
        )}
      </div>
      <div className={`font-mono tabular-nums whitespace-nowrap ${bold ? "text-2xl font-bold" : "text-xl font-semibold"}`}>
        {value}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// btnVariants — string helpers to keep buttons consistent across the app.
// Usage:  <button className={btnVariants.save}>Save</button>
// ---------------------------------------------------------------------------
const BTN_BASE =
  "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:pointer-events-none";

export const btnVariants = {
  primary: `${BTN_BASE} bg-[#1D2951] text-white hover:bg-[#2B3A67] focus:ring-[#1D2951]/40`,
  save:    `${BTN_BASE} bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500/50`,
  submit:  `${BTN_BASE} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500/50`,
  edit:    `${BTN_BASE} bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-400/50`,
  danger:  `${BTN_BASE} bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-500/50`,
  export:  `${BTN_BASE} bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-300 focus:ring-slate-400/40 shadow-none`,
  ghost:   `${BTN_BASE} bg-transparent text-slate-700 hover:bg-slate-100 shadow-none`,
  outline: `${BTN_BASE} bg-white text-[#1D2951] border border-[#1D2951]/30 hover:bg-[#1D2951]/5 focus:ring-[#1D2951]/30 shadow-none`,
};
